import TextField from 'components/form-components/TextField';
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import { calculationsOptions, chartTypeOptions } from '../utils';
import ImageUploadCard from './ImageUploadCard';
import TextLink from './TextLink';
import { generateTextWithSuffix } from 'components/utils';
import NumberField from 'components/form-components/NumberField';

const BasicTab = ({
  values,
  errors,
  touched,
  handleChange,
  dimensionsOptions,
  dimensionsLookup,
  dashboardOptions,
  imageWidget,
  setImageWidget,
  editMode,
}) => {
  const { t } = useTranslation();

  const getDimensionSelectDisplayValue = useCallback(
    (selected, fallback) => {
      const displayValues = selected
        .map((id) => dimensionsLookup[id]?.displayValue)
        .filter(Boolean);
      return generateTextWithSuffix(displayValues, 2, (count) => ` +${count} more`, fallback, t);
    },
    [t],
  );

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='w-100'>
        <label className='theme-text-black-1 theme-size-1_1 theme-font-inter-medium mb-1'>
          {t('field_widget_type')}
        </label>
        <div className='p-3 theme-background-white-1 rounded-sm'>
          <MultiLevelSelect
            name={'chartType'}
            options={chartTypeOptions}
            value={[]}
            handleChange={(name, value) => handleChange(name, value[0])}
            classes='theme-font-inter-regular'
            toggleClasses='theme-height-44 d-flex justify-content-between align-items-center px-2 py-1 border-grey-1-h-1 rounded-sm cursor-pointer'
            toggleComponent={
              <>
                <label className='theme-size-0_9 cursor-pointer'>
                  {t(
                    chartTypeOptions.children.find((o) => o.value === values.chartType)
                      ?.displayValue || 'select_select',
                  )}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
            fullWidth
            disabled={editMode}
          />
        </div>
      </div>

      {'calculation' in values && (
        <div className='w-100'>
          <label className='theme-text-black-1 theme-size-1_1 theme-font-inter-medium mb-1'>
            {t('field_calculations')}
          </label>
          <span className='lh-1 theme-size-1_5 theme-text-red-1 px-1'>*</span>
          <div className='p-3 theme-background-white-1 rounded-sm'>
            <MultiLevelSelect
              name={'calculation'}
              options={calculationsOptions}
              value={[]}
              handleChange={(name, value) => handleChange(name, value[0])}
              classes='theme-font-inter-regular'
              toggleClasses='theme-height-44 d-flex justify-content-between align-items-center px-2 py-1 border-grey-1-h-1 rounded-sm cursor-pointer'
              toggleComponent={
                <>
                  <label className='theme-size-0_9 cursor-pointer'>
                    {t(
                      calculationsOptions.children.find((o) => o.value === values.calculation)
                        ?.displayValue || 'select_select',
                    )}
                  </label>
                  <img src={chevronGreyIcon} className='rotate-180' />
                </>
              }
              fullWidth
            />
          </div>
        </div>
      )}

      {values.calculation === 'negativeAverageScore' && (
        <div className='w-100'>
          <label className='theme-text-black-1 theme-size-1_1 theme-font-inter-medium mb-1'>
            {t('field_negative_score_percentage')}
          </label>
          <span className='lh-1 theme-size-1_5 theme-text-red-1 px-1'>*</span>
          <div className='p-3 theme-background-white-1 rounded-sm'>
            <NumberField
              name={'negativeScorePercentage'}
              value={values.negativeScorePercentage}
              handleChange={(field, value) => {
                if (value) handleChange(field, Number(value));
              }}
              shrinkFieldOnly
              classes='w-100'
            />
          </div>
        </div>
      )}

      {'dimensions' in values && (
        <div className='w-100'>
          <label className='theme-text-black-1 theme-size-1_1 theme-font-inter-medium mb-1'>
            {t('field_dimensions')}
          </label>
          <span className='lh-1 theme-size-1_5 theme-text-red-1 px-1'>*</span>
          <div className='d-flex flex-column gap-3 p-3 theme-background-white-1 rounded-sm'>
            {values.dimensions.map((_, index) => (
              <MultiLevelSelect
                key={index}
                name={`dimensions[${index}]`}
                options={dimensionsOptions}
                value={values.dimensions[index] || []}
                handleChange={(name, value, displayValue, path) =>
                  handleChange(name, value, path, index)
                }
                classes='theme-font-inter-regular'
                toggleClasses='theme-height-44 d-flex justify-content-between align-items-center px-2 py-1 border-grey-1-h-1 rounded-sm cursor-pointer'
                toggleComponent={
                  <>
                    <label className='theme-size-0_9 cursor-pointer'>
                      {t(
                        getDimensionSelectDisplayValue(
                          values.dimensions[index] || [],
                          'select_select',
                        ),
                      )}
                    </label>
                    <img src={chevronGreyIcon} className='rotate-180' />
                  </>
                }
                fullWidth
              />
            ))}
          </div>
        </div>
      )}

      {'label' in values && (
        <div>
          <label className='theme-text-black-1 theme-size-1_1 theme-font-inter-medium mb-1'>
            {t('field_label')}
          </label>
          <div className='p-3 theme-background-white-1 rounded-sm'>
            <TextField
              name='label'
              classes='w-auto'
              value={values.label}
              error={errors.label}
              touched={touched.label}
              handleChange={handleChange}
              placeholder='-'
              disabled
              shrinkFieldOnly
            />
          </div>
        </div>
      )}

      {'caption' in values && (
        <div>
          <label className='theme-text-black-1 theme-size-1_1 theme-font-inter-medium mb-1'>
            {t('field_caption')}
          </label>
          <div className='p-3 theme-background-white-1 rounded-sm'>
            <TextField
              name='caption'
              classes='w-auto'
              value={values.caption}
              error={errors.caption}
              touched={touched.caption}
              handleChange={handleChange}
              placeholder='-'
              shrinkFieldOnly
            />
          </div>
        </div>
      )}

      {'textLinks' in values && (
        <TextLink
          value={values.textLinks}
          dashboardOptions={dashboardOptions}
          onChange={(list) => handleChange('textLinks', list)}
        />
      )}

      {'widgetImage' in values && (
        <ImageUploadCard
          value={values.widgetImage}
          name='widgetImage'
          onChange={handleChange}
          imageWidget={imageWidget}
          setImageWidget={setImageWidget}
        />
      )}
    </div>
  );
};

BasicTab.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  dimensionsOptions: PropTypes.object.isRequired,
  dimensionsLookup: PropTypes.object.isRequired,
  dashboardOptions: PropTypes.object.isRequired,
  imageWidget: PropTypes.object.isRequired,
  setImageWidget: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
};

export default BasicTab;
